import type { Dispatch, SetStateAction } from 'react';

import type { CartUI } from '@libs/common-node';
import type { CartNotification } from '@libs/common-types';
import { CartNotificationAction } from '@libs/core';

import { CART_ERROR_MESSAGES, COMPONENTS } from '../constants';
import { CartValidationError } from '../providers/utils/error/cartValidationError';
import type { CartValidationErrorDetails } from '../types';
import {
    checkHasBannerOverridingSchedule,
    filterScheduledDeliveryNotificationsForValidationErrors,
    getNotificationsFromCartValidationResponse,
    hasValidationErrorCode,
    isScheduledDeliveryDateUpdatedNotification,
    mergeValidationNotificationsForSingleUpdateCycle,
} from './cartValidationNotificationUtils';
import { CartValidationErrorKey } from './notificationMaps';
import { logConsoleError } from './sentryLogger';

export {
    filterScheduledDeliveryNotificationsForValidationErrors,
    getNotificationsFromCartValidationResponse,
    mergeValidationNotificationsForSingleUpdateCycle,
} from './cartValidationNotificationUtils';

const scheduledDeliveryInvalidationErrorCodes = [
    CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_INVALID_FULFILLMENT_METHOD,
    CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_OUT_OF_ALLOWED_RANGE,
    CartValidationErrorKey.AUTOSHIP_FUTURE_START_DATE_ERROR,
] as const;

export function shouldRefetchCartAfterScheduledDeliveryInvalidation(
    parsedErrors: CartValidationErrorDetails[],
    validationNotifications: CartNotification[]
): boolean {
    if (checkHasBannerOverridingSchedule(parsedErrors)) {
        return false;
    }

    const hasScheduledDeliveryInvalidationError = scheduledDeliveryInvalidationErrorCodes.some(errorCode =>
        hasValidationErrorCode(parsedErrors, errorCode)
    );
    const hasScheduledDateUpdatedNotification = validationNotifications.some(
        isScheduledDeliveryDateUpdatedNotification
    );

    return hasScheduledDeliveryInvalidationError || hasScheduledDateUpdatedNotification;
}

const getScheduledDeliveryDatesByLineItemId = (cart: CartUI): Map<string, string | null | undefined> => {
    const datesByLineItemId = new Map<string, string | null | undefined>();

    for (const item of cart.fulfillmentGroups?.flatMap(group => group.items ?? []) ?? []) {
        datesByLineItemId.set(item.id, item.scheduledDeliveryDate);
    }

    return datesByLineItemId;
};

const PENDING_SCHEDULED_DELIVERY_SNAPSHOT_STORAGE_KEY = 'pendingScheduledDeliverySnapshot';

type PendingScheduledDeliverySnapshot = {
    cartId: string;
    lineItems: Array<{
        lineItemId: string;
        scheduledDeliveryDate: string;
    }>;
};

export function savePendingScheduledDeliverySnapshot(cart: CartUI): void {
    if (globalThis.window === undefined) {
        return;
    }

    const lineItems = [...getScheduledDeliveryDatesByLineItemId(cart).entries()]
        .filter((entry): entry is [string, string] => Boolean(entry[1]))
        .map(([lineItemId, scheduledDeliveryDate]) => ({ lineItemId, scheduledDeliveryDate }));

    try {
        if (lineItems.length === 0) {
            globalThis.sessionStorage.removeItem(PENDING_SCHEDULED_DELIVERY_SNAPSHOT_STORAGE_KEY);
            return;
        }

        globalThis.sessionStorage.setItem(
            PENDING_SCHEDULED_DELIVERY_SNAPSHOT_STORAGE_KEY,
            JSON.stringify({ cartId: cart.id, lineItems } satisfies PendingScheduledDeliverySnapshot)
        );
    } catch {
        // Storage can be unavailable in restricted browser contexts; cart navigation should still continue.
    }
}

export function consumePendingScheduledDeliveryDateChange(cart: CartUI): boolean {
    if (globalThis.window === undefined) {
        return false;
    }

    try {
        const serializedSnapshot = globalThis.sessionStorage.getItem(PENDING_SCHEDULED_DELIVERY_SNAPSHOT_STORAGE_KEY);

        if (!serializedSnapshot) {
            return false;
        }

        globalThis.sessionStorage.removeItem(PENDING_SCHEDULED_DELIVERY_SNAPSHOT_STORAGE_KEY);

        const snapshot = JSON.parse(serializedSnapshot) as Partial<PendingScheduledDeliverySnapshot>;
        if (snapshot.cartId !== cart.id || !Array.isArray(snapshot.lineItems)) {
            return false;
        }

        const currentDates = getScheduledDeliveryDatesByLineItemId(cart);

        return snapshot.lineItems.some(
            ({ lineItemId, scheduledDeliveryDate }) => currentDates.get(lineItemId) !== scheduledDeliveryDate
        );
    } catch {
        return false;
    }
}

export function hasScheduledDeliveryScheduleChanged(before: CartUI, after: CartUI): boolean {
    const beforeDates = getScheduledDeliveryDatesByLineItemId(before);
    const afterDates = getScheduledDeliveryDatesByLineItemId(after);

    for (const [lineItemId, beforeDate] of beforeDates.entries()) {
        // Missing line items are removals (e.g. discontinued), not a schedule change.
        if (!beforeDate || !afterDates.has(lineItemId)) {
            continue;
        }

        if (beforeDate !== afterDates.get(lineItemId)) {
            return true;
        }
    }

    return false;
}

export function replaceWithLineItemAgnosticScheduledDeliveryDateUpdatedNotification(
    notifications: CartNotification[] | null | undefined
): CartNotification[] {
    const withoutScheduledDeliveryUpdated =
        notifications?.filter(notification => !isScheduledDeliveryDateUpdatedNotification(notification)) ?? [];

    return [
        ...withoutScheduledDeliveryUpdated,
        { action: CartNotificationAction.SCHEDULED_DELIVERY_DATE_UPDATED } as CartNotification,
    ];
}

export type ApplyScheduledDeliveryValidationRecoveryParams = {
    error: unknown;
    parsedErrors: CartValidationErrorDetails[];
    cartBeforeRefetch?: CartUI;
    cartId?: string;
    setCartNotifications?: (notifications: SetStateAction<CartNotification[] | undefined>) => void;
    refetchCart?: (options?: {
        preserveUpdatingState?: boolean;
        mergePersistedNotifications?: boolean;
    }) => Promise<CartUI | null>;
    setCart?: Dispatch<SetStateAction<CartUI | null>>;
};

export async function applyScheduledDeliveryValidationRecovery({
    error,
    parsedErrors,
    cartBeforeRefetch,
    cartId,
    setCartNotifications,
    refetchCart,
    setCart,
}: ApplyScheduledDeliveryValidationRecoveryParams): Promise<{
    didSetNotifications: boolean;
    didRefetch: boolean;
    didNormalizeScheduledDeliveryBanner: boolean;
}> {
    if (!(error instanceof CartValidationError)) {
        return { didSetNotifications: false, didRefetch: false, didNormalizeScheduledDeliveryBanner: false };
    }

    const validationNotifications = getNotificationsFromCartValidationResponse(error.errorDetails);
    const notificationsToSet = filterScheduledDeliveryNotificationsForValidationErrors(
        parsedErrors,
        validationNotifications
    );

    let didSetNotifications = false;
    let didNormalizeScheduledDeliveryBanner = false;
    if (notificationsToSet.length > 0 && setCartNotifications) {
        setCartNotifications(prev =>
            mergeValidationNotificationsForSingleUpdateCycle(prev ?? null, notificationsToSet)
        );
        didSetNotifications = true;
    }

    // Checkout validate uses silentUpdate, so the server may mutate scheduled delivery even when the
    // 422 body only includes unrelated errors (e.g. out of stock). Refetch to sync cart + notifications.
    const shouldRefetch = Boolean(cartId && refetchCart && setCart);

    if (!shouldRefetch || !refetchCart || !setCart) {
        return { didSetNotifications, didRefetch: false, didNormalizeScheduledDeliveryBanner };
    }

    try {
        const refreshedCart = await refetchCart({
            preserveUpdatingState: true,
            mergePersistedNotifications: true,
        });

        if (!refreshedCart) {
            return { didSetNotifications, didRefetch: false, didNormalizeScheduledDeliveryBanner };
        }

        setCart(refreshedCart);

        const shouldNormalizeScheduledDeliveryBanner =
            !checkHasBannerOverridingSchedule(parsedErrors) &&
            (validationNotifications.some(isScheduledDeliveryDateUpdatedNotification) ||
                (refreshedCart.notifications ?? []).some(isScheduledDeliveryDateUpdatedNotification) ||
                (cartBeforeRefetch != null && hasScheduledDeliveryScheduleChanged(cartBeforeRefetch, refreshedCart)));

        if (shouldNormalizeScheduledDeliveryBanner && setCartNotifications) {
            setCartNotifications(prev =>
                replaceWithLineItemAgnosticScheduledDeliveryDateUpdatedNotification(prev ?? null)
            );
            didSetNotifications = true;
            didNormalizeScheduledDeliveryBanner = true;
        }

        return { didSetNotifications, didRefetch: true, didNormalizeScheduledDeliveryBanner };
    } catch (refreshError) {
        logConsoleError(JSON.stringify(refreshError instanceof Error ? refreshError.message : String(refreshError)), {
            component: COMPONENTS.CART_PROVIDER,
            severity: 'HIGH',
            additionalInfo: `${CART_ERROR_MESSAGES.CART_UPDATE_FAILED} | Cart refetch failed after scheduled delivery invalidation`,
        });
        return { didSetNotifications, didRefetch: false, didNormalizeScheduledDeliveryBanner };
    }
}
